type ErrorMetadata = {
  code?: string;
  status?: number;
  details?: unknown;
};

export class AppServiceError extends Error {
  readonly code?: string;
  readonly status?: number;
  readonly details?: unknown;

  constructor(message: string, metadata: ErrorMetadata = {}) {
    super(message);
    this.name = "AppServiceError";
    this.code = metadata.code;
    this.status = metadata.status;
    this.details = metadata.details;
  }
}

export class AuthenticationRequiredError extends AppServiceError {
  constructor(message = "Authentication is required.", metadata: ErrorMetadata = {}) {
    super(message, metadata);
    this.name = "AuthenticationRequiredError";
  }
}

export class AuthUnavailableError extends AppServiceError {
  constructor(message = "Authentication service is unavailable.", metadata: ErrorMetadata = {}) {
    super(message, metadata);
    this.name = "AuthUnavailableError";
  }
}

export class BavimailRequestError extends AppServiceError {
  constructor(message = "Bavimail request failed.", metadata: ErrorMetadata = {}) {
    super(message, metadata);
    this.name = "BavimailRequestError";
  }
}

type AuthErrorLike = {
  message: string;
  status: number;
  statusText: string;
  code?: string;
};

export function mapAuthError(error: AuthErrorLike, fallbackMessage: string) {
  if (error.status === 401 || error.status === 404) {
    return new AuthenticationRequiredError(error.message || fallbackMessage, {
      code: error.code,
      status: error.status,
      details: error,
    });
  }

  if (error.status === 503 || error.code === "AUTH_SERVICE_UNAVAILABLE") {
    return new AuthUnavailableError(error.message || fallbackMessage, {
      code: error.code,
      status: error.status,
      details: error,
    });
  }

  return new AppServiceError(error.message || fallbackMessage, {
    code: error.code,
    status: error.status,
    details: error,
  });
}

type BackendErrorLike = {
  message: string;
  status: number;
  statusText: string;
  code?: string;
  details?: unknown;
};

export function mapBackendError(error: BackendErrorLike, fallbackMessage: string) {
  const message = error.message || fallbackMessage;
  const metadata = { code: error.code, status: error.status, details: error.details };

  if (error.status === 401) {
    return new AuthenticationRequiredError(message, metadata);
  }

  if (error.status === 503 && error.code === "auth_not_configured") {
    return new AuthUnavailableError(message, metadata);
  }

  return new BavimailRequestError(message, metadata);
}

export function mapBavimailError(error: unknown, fallbackMessage: string) {
  if (error instanceof AppServiceError) {
    return error;
  }

  return new BavimailRequestError(getErrorMessage(error, fallbackMessage), {
    details: error,
  });
}

export function getErrorMessage(error: unknown, fallbackMessage: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return fallbackMessage;
}

export function isAuthUnavailableError(error: unknown): error is AuthUnavailableError {
  if (!error || typeof error !== "object") {
    return false;
  }

  const namedError = error as { name?: unknown; code?: unknown };
  return (
    namedError.name === "AuthUnavailableError" || namedError.code === "AUTH_SERVICE_UNAVAILABLE"
  );
}
